/**
 * Procedural sounds. A car horn is two diaphragm horns a major third apart: buzzy, odd-and-even
 * harmonics, shaped by the horn's flare into a formant around 1.5–3 kHz. Here: two sawtooths through a
 * soft clipper, a band-pass and a presence peak, with a fast attack (the pitch settles upward as the
 * diaphragm starts) and a short release.
 */
let curve: Float32Array<ArrayBuffer> | null = null;

function clipCurve(): Float32Array<ArrayBuffer> {
  if (curve) return curve;
  const n = 1024;
  curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * 2.5) / Math.tanh(2.5);
  }
  return curve;
}

/**
 * Sound a horn into `dest` from `when` for `dur` s. `pitch` scales the base note (cars differ: 0.8
 * trucks … 1.25 small cars); `beeps` > 1 gives a quick double or triple toot.
 */
export function horn(ctx: BaseAudioContext, dest: AudioNode, when: number, dur: number, pitch = 1, gain = 1, beeps = 1): void {
  const f = 370 * pitch;
  const env = ctx.createGain();
  env.gain.value = 0;
  const shaper = ctx.createWaveShaper();
  shaper.curve = clipCurve();
  shaper.oversample = '2x';
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1500 * Math.sqrt(pitch);
  bp.Q.value = 0.7;
  const peak = ctx.createBiquadFilter();
  peak.type = 'peaking';
  peak.frequency.value = 2900;
  peak.Q.value = 1.4;
  peak.gain.value = 5;
  // The clipper leaves DC and a low difference tone between the two notes; horns have little below
  // 300 Hz.
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 300;
  hp.Q.value = 0.7;
  const pre = ctx.createGain();
  pre.gain.value = 0.45;
  pre.connect(shaper);
  shaper.connect(bp);
  bp.connect(peak);
  peak.connect(hp);
  hp.connect(env);
  env.connect(dest);
  const gap = 0.09;
  const each = beeps > 1 ? Math.min(dur, 0.16) : dur;
  const end = when + beeps * each + (beeps - 1) * gap;
  for (const [k, detune] of [
    [1, -4],
    [1.26, 5],
  ]) {
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.detune.value = detune;
    for (let b = 0; b < beeps; b++) {
      const t = when + b * (each + gap);
      o.frequency.setValueAtTime(f * k * 0.965, t);
      o.frequency.exponentialRampToValueAtTime(f * k, t + 0.035);
    }
    o.connect(pre);
    o.start(when);
    o.stop(end + 0.1);
    o.onended = () => {
      o.disconnect();
      if (k === 1) env.disconnect();
    };
  }
  for (let b = 0; b < beeps; b++) {
    const t = when + b * (each + gap);
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(gain, t + 0.012);
    env.gain.setValueAtTime(gain, t + each - 0.04);
    env.gain.linearRampToValueAtTime(0, t + each);
  }
}
